import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { InventoryService } from '@app/services/inventory/inventory.service';
import * as CountsActions from '../actions/counts.actions';
import { Store } from '@ngrx/store'
import { ToastrService } from 'ngx-toastr';
import { loadMostRecent, loadMostRecentBasic } from '../actions/counts.actions';
import { LimitAccessService } from '@app/core/services/limit-access.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class CountsEffects {

  loadCounts$ = createEffect(() => {
    return this.actions$.pipe( 

      ofType(CountsActions.loadCounts),
      switchMap((action) =>
        /** An EMPTY observable only emits completion. Replace with your own observable API request */
        this._inventory.onGetInventory(action.active, action.direction, action.locations ).pipe(
          map(response => {
            const { inventory, total } = response;
            return CountsActions.loadCountsSuccess({ inventory, total });
          }),
          catchError(error => of(CountsActions.loadCountsFailure({ error }))))
      )
    );
  });

  loadInventoryItem$ = createEffect(() => {
    return this.actions$.pipe( 

      ofType(CountsActions.loadInventoryItem),
      switchMap((action) =>
        this._inventory.getInventoryItem(action.invitmID).pipe(
          map(inventory => CountsActions.loadInventoryItemSuccess(inventory)),
          catchError(error => of(CountsActions.loadInventoryItemFailure({ error }))))
      )
    );
  });

  loadFinalizedCounts$ = createEffect(() => {
    return this.actions$.pipe( 

      ofType(CountsActions.loadFinalizedCounts),
      switchMap((action) =>
        this._inventory.getFinalizedInventory(action.active, action.direction, action.currentPage, action.recordsByPage, action.locations).pipe(
          map(({ complete })=> {  
            return CountsActions.loadFinalizedCountsSuccess(complete)
          }),
          catchError(error => of(CountsActions.loadFinalizedCountsFailure({ error }))))
      )
    );
  });

  deleteCount$ = createEffect(() => {
    return this.actions$.pipe( 

      ofType(CountsActions.deleteCount),
      switchMap((action) =>
        this._inventory.onDelete(action.invID).pipe(
          tap(() => this._toastr.error(`${this._translation.instant('successfullyDeleted')} ${action.invBatchName}.`)),
          map(() => CountsActions.deleteCountSuccess({ invID : action.invID})),
          catchError(error => of(CountsActions.loadCountsFailure({ error }))))
      )
    );
  });

  loadCount$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CountsActions.loadCount),
      switchMap((action) =>
        this._inventory.onGetCount(action.invID, action.active, action.direction).pipe(
          map((data) => CountsActions.loadCountSuccess({ data })),
          catchError(error => of(CountsActions.loadCountsFailure({ error }))))
      )
    );
  });

  loadFinancial$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CountsActions.loadFinancial),
      switchMap((action) =>
        this._inventory.onGetFinancial(action.invID, action.active, action.direction).pipe(
          map((data) => CountsActions.loadFinancialSuccess({ data })),
          catchError(error => of(CountsActions.loadFinancialFailure({ error }))))
      )
    );
  });

  deleteProduct$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CountsActions.deleteProduct),
      switchMap(action => {
        return this._inventory.onDeleteProduct(action.invID, action.prdID).pipe(
          tap(() => {
          }),
          map((): any => {
            return CountsActions.deleteProductSuccess({ prdID: action.prdID })
          }),
          catchError(error => {
            return of(CountsActions.deleteProductFailure({ error: error.message }))
          })
        )
      })
    );
  });

  loadReorder = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CountsActions.loadReorder),
      switchMap((action) =>
        this._inventory.onGetReorder(action.invID, action.active, action.direction, action.pcmType).pipe(
          map((data) => CountsActions.loadReorderSuccess({ data })),
          catchError(error => of(CountsActions.loadReorderFailure({ error }))))
      )
    );
  });

  loadPO = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CountsActions.loadPO),
      switchMap((action) =>
        this._inventory.onGetPOWorkSheet(action.prdMinQty, action.invID).pipe(
          map((data) => CountsActions.loadPOSuccess({ data })),
          catchError(error => of(CountsActions.loadPOFailure({ error }))))
      )
    );
  });

  loadPS = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CountsActions.loadPS),
      switchMap((action) =>
        this._inventory.onGetPSWorkSheet(action.prdMinQty, action.invID).pipe(
          map((data) => CountsActions.loadPSSuccess({ data })),
          catchError(error => of(CountsActions.loadPSFailure({ error }))))
      )
    );
  });

  loadPrintCount = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CountsActions.loadPrintCount),
      switchMap((action) =>
        this._inventory.onGetPrintCount(action.invID, action.active, action.direction).pipe(
          map((data) => CountsActions.loadPrintCountSuccess({ data })),
          catchError(error => of(CountsActions.loadPrintCountFailure({ error }))))
      )
    );
  });

  loadExcel = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CountsActions.loadExcel),
      switchMap((action) =>
        this._inventory.onGetExcel(action.invID).pipe(
          map((data) => CountsActions.loadExcelSuccess({ data })),
          catchError(error => of(CountsActions.loadExcelFailure({ error }))))
      )
    );
  });

  finalizeCounts = createEffect(() => {
    return this.actions$.pipe(
      ofType(CountsActions.finalizeCounts),
      switchMap((action) =>
        this._inventory.finalizeCounts(
          action.data,
          action.overrideOnhand,
          action.overrideMessage,
          action.overrideItems
        ).pipe(
          map((data) => {
            return CountsActions.finalizeCountsSuccess({ data })
          }),
          catchError(error => of(CountsActions.finalizeCountsFailure({ error })))
          )
      )
    );
  });

  searchFinalizedCounts = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CountsActions.searchFinalizedCounts),
      switchMap((action) =>
        this._inventory.searchFinalizedCounts(action.query, action.currentPage, action.recordsByPage, action.locations).pipe(
          map(({complete}) => {
            return CountsActions.searchFinalizedCountsSuccess(complete)
          }),
          catchError(error => of(CountsActions.searchFinalizedCountsFailure({ error })))
          )
      )
    );
  });

  searchDraftCounts = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CountsActions.searchDraftCounts),
      switchMap((action) => {
        let direction: string;
        let sort: string;
      
        return this._inventory.searchDraftCounts('invBatchName', 'ASC', action.query, action.locations).pipe(
          map((data): any => {
            return CountsActions.searchDraftCountsSuccess({ inventory: data.inventory, total: data.total })
          }),
          catchError(error => {
            return of(CountsActions.searchDraftCountsFailure({ error }))
          })
        )
        }
      )
    );
  });

  draftCounts = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CountsActions.draftCounts),
      switchMap((action) =>
        this._inventory.draftCounts(action.data).pipe(
          map((data) => {
            return CountsActions.draftCountsSuccess(data);
          }),
          catchError(error => of(CountsActions.draftCountsFailure({ error })))
          )
      )
    );
  });

  loadMostRecent = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CountsActions.loadMostRecent),
      switchMap((action) =>
        this._inventory.getMostRecentCounts().pipe(
          map((data) => CountsActions.loadMostRecentSuccess({ data })),
          catchError(error => of(CountsActions.loadMostRecentFailure({ error }))))
      )
    );
  });

  loadMostRecentBasic = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CountsActions.loadMostRecentBasic),
      switchMap((action) =>
        this._inventory.getMostRecentCountsBasic().pipe(
          map((data) => CountsActions.loadMostRecentBasicSuccess({ data })),
          catchError(error => of(CountsActions.loadMostRecentBasicFailure({ error }))))
      )
    );
  });

  removeMostRecent = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CountsActions.removeMostRecent),
      switchMap((action) =>
        this._inventory.onDelete(action.invID).pipe(

          map(() => CountsActions.removeMostRecentSuccess({ invID: action.invID })),
          tap(() => {
            if (this.resultTierIdExpose === 1) {
              this._store.dispatch(loadMostRecentBasic());
            } else {
              this._store.dispatch(loadMostRecent());
            }
          }),
          catchError(error => of(CountsActions.removeMostRecentFailure({ error }))))
      )
    );
  });

  public resultTierIdExpose: any = 0;

  constructor(
    private actions$: Actions, 
    private _inventory: InventoryService, 
    private _store: Store, 
    private _toastr: ToastrService, 
    private _translation: TranslateService,
    private limitAccessService: LimitAccessService
  ) {
    this.limitAccessService.tierID$.subscribe((tierID) => {
      if (tierID) {
        let resultTierID = this.limitAccessService.getTierId();
        this.resultTierIdExpose = resultTierID; 
      }
    });
  }

}
